import { OfficeHoursApiResponse } from '../../types';
import { getCustomerIdFromToken } from '../../utils/getCustomerIdFromToken';
import { post } from '../apiService';

const OFFICE_HOURS_LIST_TTL_MS = 5000;
const officeHoursListCache = new Map<
  string,
  {
    timestamp: number;
    result: { data: OfficeHoursApiResponse; loading: boolean };
  }
>();
const officeHoursListInFlight = new Map<
  string,
  Promise<{ data: OfficeHoursApiResponse; loading: boolean }>
>();

export const invalidateOfficeHoursListCache = (): void => {
  officeHoursListCache.clear();
  officeHoursListInFlight.clear();
};

export const postOfficeHoursList = async (
  practiceIds: string[],
): Promise<{
  data: OfficeHoursApiResponse;
  loading: boolean;
}> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer Id is not available');
  }
  if (!practiceIds) {
    throw new Error('Please send practice ids');
  }

  const normalizedPracticeIds = [...practiceIds].sort();
  const cacheKey = `${customer}:${normalizedPracticeIds.join(',')}`;
  const now = Date.now();
  const cached = officeHoursListCache.get(cacheKey);
  if (cached && now - cached.timestamp < OFFICE_HOURS_LIST_TTL_MS) {
    return cached.result;
  }

  const inFlight = officeHoursListInFlight.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const url = `resourceMatrix/office_hour/list?customerId=${customer}`;
  let loading = true;
  const request = (async () => {
    try {
      const body = {
        practiceIds,
      };
      const responseData = await post(url, body);
      const data = responseData as OfficeHoursApiResponse;
      loading = false;
      const result = { data, loading };
      officeHoursListCache.set(cacheKey, {
        timestamp: Date.now(),
        result,
      });
      return result;
    } catch (error) {
      loading = false;
      // Provide more specific error information for debugging
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unknown error occurred';
      console.error(
        'postOfficeHoursList error:',
        errorMessage,
        error,
      );
      throw new Error(
        `Failed to fetch office hours: ${errorMessage}`,
      );
    }
  })();

  officeHoursListInFlight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    officeHoursListInFlight.delete(cacheKey);
  }
};
